import { FC, memo, useCallback } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, styled } from '@mui/material';
import clsx from 'clsx';

import { useModal } from '@/hooks';
import { ModalProvider } from '@/providers';
import { isUndefined } from '@/utils';

import { ModalProps } from './types';

import styles from './styles.module.scss';

import CloseIcon from '@/assets/icons/close.svg?react';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    position: 'absolute',
    minHeight: '205px',
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  padding: '8px 38x',
  border: 'none',
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '22px',
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '& .MuiButtonBase-root': {
    position: 'static',
  },
});

const StyledDialogContent = styled(DialogContent)({
  margin: '0',
  overflow: 'hidden',
  wordWrap: 'break-word',
  position: 'relative',
  width: '100%',
  border: 'none',
  padding: 0,
  height: '100%',
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '22px',
  color: '#282828',
  '& .MuiDialogContent-root': {
    borderTop: '1px #eff2f7 solid',
    padding: '0 38px',
  },
});

const StyledDialogActions = styled(DialogActions)({
  padding: '24px 38px 38px 38px',
  width: '100%',
  display: 'flex',
});

const StyledCloseButton = styled(IconButton)({
  color: '#7A89A7',
  marginRight: -10,
});

const StyledCancelButton = styled(Button)({
  height: '40px',
  border: '2px solid #EFF2F7',
  fontSize: '20px',
  '&:hover': {
    border: '2px solid #EFF2F7',
    filter: 'drop-shadow(0px 2px 15px rgba(122, 137, 167, 0.25))',
    borderRadius: '4px',
    background: 'white',
    color: '#282828',
    boxShadow: 'none',
  },
});

const StyledSubmitButton = styled(Button)({
  height: '40px',
  backgroundColor: '#52B36B',
  color: '#fff',
  '&:hover': {
    boxShadow: 'none',
    opacity: 0.8,
    backgroundColor: '#52B36B',
    filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
  },
});

export const ModalComponent = memo<ModalProps>(
  ({
    showSubmitBtnExternal,
    showCancelBtnExternal,
    showCloseBtnExternal,
    isOpenModal,
    children,
    title,
    onClose,
    onSubmit,
    disabledSubmitBtn,
    color,
    className,
  }) => {
    const { showCancelBtn, showSubmitBtn, showCloseBtn } = useModal();

    const isSubmitBtnVisible = isUndefined(showSubmitBtnExternal) ? showSubmitBtn : showSubmitBtnExternal;
    const isCancelBtnVisible = isUndefined(showCancelBtnExternal) ? showCancelBtn : showCancelBtnExternal;
    const isCloseBtnVisible = isUndefined(showCloseBtnExternal) ? showCloseBtn : showCloseBtnExternal;

    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    const handleSubmit = useCallback(() => {
      onSubmit?.();
    }, [onSubmit]);

    return (
      <StyledDialog
        open={isOpenModal}
        className={className}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <StyledDialogTitle id="alert-dialog-slide-title">
          <span className={styles.title}>{title}</span>

          {isCloseBtnVisible && (
            <StyledCloseButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </StyledCloseButton>
          )}
        </StyledDialogTitle>

        <StyledDialogContent>{children}</StyledDialogContent>

        {isCancelBtnVisible && isSubmitBtnVisible && (
          <StyledDialogActions>
            {isCancelBtnVisible && (
              <StyledCancelButton onClick={handleClose} size="large" variant="contained" color="secondary">
                <span className={clsx(styles.btn, styles.btn__blue)}>Cancel</span>
              </StyledCancelButton>
            )}

            {isSubmitBtnVisible && (
              <StyledSubmitButton
                onClick={handleSubmit}
                size="large"
                disabled={disabledSubmitBtn}
                variant="contained"
                color={color || 'secondary'}
              >
                <span className={styles.btn}>Submit</span>
              </StyledSubmitButton>
            )}
          </StyledDialogActions>
        )}
      </StyledDialog>
    );
  },
);

export const Modal: FC<ModalProps> = (props) => {
  return (
    <ModalProvider>
      <ModalComponent {...props} />
    </ModalProvider>
  );
};
